import {CharacterMap} from '../type/CharacterMap.interface';
import {SceneKey, TalkGroupKey, TalkKey} from '../type/KeyType.type';
import {ProductInfo} from '../type/ProductInfo.interface';
import {Progress} from '../type/Progress.interface';
import {SceneInfoMap} from '../type/SceneInfoMap.interface';
import {SceneMap} from '../type/SceneMap.interface';
import {TalkDataType} from '../type/TalkDataType.enum';
import {TalkGroupMap} from '../type/TalkGroupMap.interface';
import {TalkInfoMap} from '../type/TalkInfoMap.interface';
import {TalkMap} from '../type/TalkMap.interface';
import {TalkPosition} from '../type/TalkPosition.enum';
import {TalkShapeType} from '../type/TalkShapeType.enum';
import {TalkType} from '../type/TalkType.enum';
import {TalkViewerResponseVo} from '../type/TalkViewerResponse.interface';
import {TextAlign} from '../type/TextAlign.enum';

/**
 * API 로부터 받은 결과값을 내부 상태로 변환한다.
 *
 * @param productId
 * @param data API Response
 * @throws Validation 실패 시 에러 발생
 */
export function getTalkViewerInfo(
  productId: number,
  data: TalkViewerResponseVo
): {
  product: ProductInfo;
  characterMap: CharacterMap;
  sceneMap: SceneMap;
  talkGroupMap: TalkGroupMap;
  talkMap: TalkMap;
  sceneInfoMap: SceneInfoMap;
  talkInfoMap: TalkInfoMap;
} {
  const characterMap: CharacterMap = {};
  const sceneMap: SceneMap = {};
  const talkGroupMap: TalkGroupMap = {};
  const talkMap: TalkMap = {};
  const sceneIds: SceneKey[] = [];
  const sceneInfoMap: SceneInfoMap = {};
  const talkInfoMap: TalkInfoMap = {};

  data.characters.forEach(({id, name, image}) => {
    characterMap[id] = {
      id,
      name,
      image,
    };
  });

  let talkIndex = 0;
  data.scenes.forEach((scene, sceneIndex, sceneArr) => {
    sceneIds.push(scene.id);
    const talkGroupIds: TalkGroupKey[] = [];
    sceneInfoMap[scene.id] = {
      sceneId: scene.id,
      talkGroupId: scene.talkGroups[0].id,
      talkHistory: [],
    };
    scene.talkGroups.forEach(talkGroup => {
      talkGroupIds.push(talkGroup.id);
      const talkIds: TalkKey[] = [];

      // 특정 등장인물의 톡인데 등장인물 정보가 없는 경우에는 비정상 톡으로 판단해서 톡 데이터에 포함하지 않는다.
      const filteredTalks = talkGroup.talks.filter(talk =>
        talk.characterid ? !!characterMap[talk.characterid] : true
      );

      // 톡이 없는 톡그룹을 위한 처리. 쓸모없는 톡을 하나 넣어주고, 이 톡그룹을 찾는 용도로 사용한다.
      if (filteredTalks.length === 0) {
        const talkId = `dummy_${++talkIndex}`;
        talkIds.push(talkId);
        talkMap[talkId] = {
          id: talkId,
          talkIndex,
          talkType: TalkType.None,
          datatype: TalkDataType.None,
          shapeType: TalkShapeType.None,
          scripts: '',
          talkGroupId: talkGroup.id,
          productId,
          background: {
            color: 'transparent',
          },
          font: {color: 'transparent'},
          position: TalkPosition.Center,
          align: TextAlign.Center,
        };
        talkInfoMap[talkId] = {
          talkId,
          isShowReplay: false,
        };
      } else {
        filteredTalks.forEach(talk => {
          talkIds.push(talk.id);
          talkMap[talk.id] = {
            id: talk.id,
            talkIndex: ++talkIndex,
            characterId: talk.characterid,
            talkType: talk.type,
            datatype: talk.datatype,
            shapeType: talk.shapeType,
            scripts: talk.scripts || '',
            talkGroupId: talkGroup.id,
            productId,
            background: {
              color: talk.background ? talk.background.color : 'black',
              border: talk.background?.border,
            },
            font: talk.font,
            position: talk.position,
            characterInfo: talk.character,
            media: talk.media,
            effect: talk.effect,
            align: talk.align || TextAlign.Left,
          };
          talkInfoMap[talk.id] = {
            talkId: talk.id,
            isShowReplay: false,
          };
        });
      }

      talkGroupMap[talkGroup.id] = {
        id: talkGroup.id,
        background: talkGroup.background,
        floatingImage: talkGroup.floatingImage,
        floatingImageOpacity: talkGroup.floatingImageOpacity || 1,
        sceneId: scene.id,
        talkIds,
        productId,
      };
    });
    sceneMap[scene.id] = {
      id: scene.id,
      type: scene.type,
      talkGroupIds,
      productId,
      prevSceneId: sceneIndex === 0 ? undefined : sceneArr[sceneIndex - 1].id,
      nextSceneId:
        sceneIndex + 1 >= sceneArr.length
          ? undefined
          : sceneArr[sceneIndex + 1].id,
      title: scene.title,
    };
  });

  const talkCount = Object.keys(talkMap).length;

  const progress: Progress = {
    totalTalkCount: talkCount,
    visitedTalkCount: 0,
  };
  const product: ProductInfo = {
    productId,
    progress,
    sceneIds,
  };
  return {
    product,
    characterMap,
    sceneMap,
    talkGroupMap,
    talkMap,
    sceneInfoMap,
    talkInfoMap,
  };
}
